import { getSnapshot } from '@/api/requests';
import { useGameStateStore } from '../gameState';
import { usePlayFlowState } from '../playFlow';
import { useUserStore } from '../user';
import { IGame } from '@/types';
import { cloneDeep, orderBy } from 'lodash';
import { useToast } from 'vue-toastification';

const toast = useToast();

export class TGameSnapshotService {
  playFlowStore: ReturnType<typeof usePlayFlowState>;

  gameStateStore: ReturnType<typeof useGameStateStore>;

  userStore: ReturnType<typeof useUserStore>;

  gameId: number | null = null;

  intervalId: NodeJS.Timeout | null = null;

  isInitialSnapshotPending = false;

  hasSnapshotError = false;

  constructor(
    playFlowStore: ReturnType<typeof usePlayFlowState>,
    gameStateStore: ReturnType<typeof useGameStateStore>,
    userStore: ReturnType<typeof useUserStore>
  ) {
    this.playFlowStore = playFlowStore;
    this.gameStateStore = gameStateStore;
    this.userStore = userStore;
  }

  setGameId(id: number) {
    this.gameId = id;
    this.gameStateStore.snapShot.gameId = id;
  }

  startSnapshotUpdates() {
    this.intervalId = setInterval(() => {
      this.getGameSnapshot();
    }, 10000);
  }

  stopSnapshotUpdates() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  async getGameSnapshot(setInitialState = false) {
    if (!this.gameId) {
      return;
    }

    if (setInitialState) {
      this.isInitialSnapshotPending = true;
    }

    const snap = await getSnapshot(this.gameId);
    if (snap === undefined) {
      return;
    }
    if (!snap) {
      if (!this.hasSnapshotError) {
        this.hasSnapshotError = true;
        toast.error('Snapshot data did not load properly. Queued plays will wait for reconnection.', {
          position: 'bottom-center',
          timeout: 6000,
        });
      }
      return;
    }

    this.hasSnapshotError = false;
    const shouldSetInitialState = setInitialState || this.isInitialSnapshotPending;
    this.resolveSnapshotData(snap, shouldSetInitialState);
    this.isInitialSnapshotPending = false;
  }

  private resolveSnapshotData(snapshot: IGame | null, setInitialState: boolean) {
    if (!snapshot) {
      return;
    }

    const orderedPlays = orderBy(snapshot.plays || [], ['driveNumber', 'drivePlayNumber'], ['asc', 'asc']);

    if ((this.userStore.isClockScout && this.userStore.isPlayScout) || setInitialState) {
      this.setGameState(snapshot);
      this.playFlowStore.parsePlayState(snapshot.gameId, orderedPlays, setInitialState);
    } else if (!this.userStore.isClockScout && this.userStore.isPlayScout) {
      this.setGameState(snapshot);
    } else if (this.userStore.isClockScout && !this.userStore.isPlayScout) {
      this.playFlowStore.parsePlayState(snapshot.gameId, orderedPlays, setInitialState);
    }
  }

  private setGameState(snapshot: IGame | null) {
    if (snapshot.gameState) {
      this.gameStateStore.setGameStateFromSnapshot(
        this.userStore.isClockScout && this.userStore.isPlayScout,
        cloneDeep(snapshot.gameState)
      );
    }

    if (snapshot) {
      this.gameStateStore.snapShot = cloneDeep(snapshot);
    }
  }
}
